import { Validator } from 'fluentvalidation-ts';
import { orderBy, sumBy } from 'lodash';
import {
  beAnInteger,
  beBetweenInclusive,
  beGreaterThan,
  beGreaterThanOrEqualTo,
  beLessThan,
  beNumeric,
  beValidPercentage,
  haveDecimalPlaces,
} from '../../../../../validators/stringValidators';
import { BuildTypeCode } from '../../../../projects/project';
import { RoomFloorConstructionFormModel } from '../../GetDataForQuoteOrDesignForm';
import { FloorLevelCode, getFloorLevelOrderingPosition } from '../../quoteOrDesignFormData';
import { UnitHeatLossDetailsFormModel } from '../../units/heatLoss/unitHeatLossDetailsFormModel';
import { getDistinctFloorLevelCodes, UnitFormModel } from '../../units/unitFormModel';
import { RoomType } from '../roomTypes';
import {
  getDefaultRoomFloorHeatLossDetailsFormModel,
  getNewRoomFloorHeatLossDetailsFormModel,
  RoomFloorHeatLossDetailsFormModel,
  RoomFloorHeatLossDetailsFormModelValidator,
} from './roomFloorHeatLossDetailsFormModel';
import { defaultRoomAirChangeRatesByRoomType } from './roomHeatLossDefaults';
import {
  getDefaultRoofHeatLossDetailsFormModel,
  getNewRoomRoofHeatLossDetailsFormModel,
  RoomRoofHeatLossDetailsFormModel,
  RoomRoofHeatLossDetailsFormModelValidator,
} from './roomRoofHeatLossDetailsFormModel';
import {
  getNewRoomWallHeatLossDetailsFormModel,
  RoomWallHeatLossDetailsFormModel,
  RoomWallHeatLossDetailsFormModelValidator,
} from './roomWallHeatLossDetailsFormModel';

export type RoomHeatLossDetailsFormModel = {
  ceilingHeightInMetres: string;
  numberOfAirChangesPerHour: string;
  mechanicalVentilationHeatRecoveryEfficiencyPercentage: string;
  floorCoveringName: string | null;
  floorCoveringThermalResistancePerMillimetre: string;
  floorCoveringThicknessInMillimetres: string;
  deckingThermalResistancePerMillimetre: string;
  deckingThicknessInMillimetres: string;
  underlayThermalResistancePerMillimetre: string;
  underlayThicknessInMillimetres: string;
  overrideTotalHeatLossInWatts: boolean;
  userEnteredTotalHeatLossInWatts: string;
  walls: Array<RoomWallHeatLossDetailsFormModel>;
  roofs: Array<RoomRoofHeatLossDetailsFormModel>;
  floors: Array<RoomFloorHeatLossDetailsFormModel>;
};

export const getNewRoomHeatLossDetailsFormModel = (
  floorConstruction: RoomFloorConstructionFormModel | null,
  roomFloorLevelCode: FloorLevelCode | null,
  roomType: RoomType | null,
  unitHeatLossDetails: UnitHeatLossDetailsFormModel,
  unit: UnitFormModel,
  buildTypeCode: BuildTypeCode,
  externalAirTemperatureInDegreesCelsius: number,
): RoomHeatLossDetailsFormModel => ({
  ceilingHeightInMetres: defaultCeilingHeightInMetres.toString(),
  numberOfAirChangesPerHour:
    roomType == null
      ? ''
      : defaultRoomAirChangeRatesByRoomType[roomType][buildTypeCode].NumberOfAirChangesPerHour.toString(),
  mechanicalVentilationHeatRecoveryEfficiencyPercentage:
    roomType == null
      ? defaultMechanicalVentilationHeatRecoveryEfficiencyPercentage.toString()
      : defaultRoomAirChangeRatesByRoomType[roomType][buildTypeCode].mvhrEfficiencyInPercentage.toString(),
  floorCoveringName: null,
  floorCoveringThermalResistancePerMillimetre: '',
  floorCoveringThicknessInMillimetres: '',
  deckingThermalResistancePerMillimetre: getDefaultDeckingThermalResistancePerMillimetre(floorConstruction),
  deckingThicknessInMillimetres: getDefaultDeckingThicknessInMillimetres(floorConstruction),
  underlayThermalResistancePerMillimetre: '',
  underlayThicknessInMillimetres: '',
  overrideTotalHeatLossInWatts: false,
  userEnteredTotalHeatLossInWatts: '',
  walls: unitHeatLossDetails.wallElements.map(() =>
    getNewRoomWallHeatLossDetailsFormModel(externalAirTemperatureInDegreesCelsius),
  ),
  roofs: getDefaultRoofHeatLossDetails(
    unitHeatLossDetails,
    roomFloorLevelCode,
    externalAirTemperatureInDegreesCelsius,
    unit,
  ),
  floors: getDefaultFloorHeatLossDetails(
    unitHeatLossDetails,
    roomFloorLevelCode,
    externalAirTemperatureInDegreesCelsius,
    unit,
  ),
});

const defaultCeilingHeightInMetres = 2.4;
const defaultMechanicalVentilationHeatRecoveryEfficiencyPercentage = 0;

const getDefaultDeckingThermalResistancePerMillimetre = (
  floorConstruction: RoomFloorConstructionFormModel | null,
): string => floorConstruction?.defaultDeckingThermalResistancePerMillimetre?.toString() ?? '0';

const getDefaultDeckingThicknessInMillimetres = (floorConstruction: RoomFloorConstructionFormModel | null): string =>
  floorConstruction?.defaultDeckingThicknessInMillimetres?.toString() ?? '0';

const getDefaultFloorHeatLossDetails = (
  unitHeatLossDetails: UnitHeatLossDetailsFormModel,
  roomFloorLevelCode: FloorLevelCode | null,
  externalAirTemperatureInDegreesCelsius: number,
  unit: UnitFormModel,
): Array<RoomFloorHeatLossDetailsFormModel> => {
  // Pre-populate room floor details with most likely scenario
  const defaultFloorNames = ['Ground Element 1', 'Upper Element 1'];
  if (
    unitHeatLossDetails.floorElements.length === 2 &&
    defaultFloorNames.includes(unitHeatLossDetails.floorElements[0].name) &&
    defaultFloorNames.includes(unitHeatLossDetails.floorElements[1].name)
  ) {
    const distinctFloorLevelCodesInOrder = orderBy(getDistinctFloorLevelCodes(unit), getFloorLevelOrderingPosition);
    const groundIndex = unitHeatLossDetails.floorElements.findIndex(floor => floor.name === 'Ground Element 1');
    const upperIndex = unitHeatLossDetails.floorElements.findIndex(floor => floor.name === 'Upper Element 1');

    const defaultFloors = [
      getDefaultRoomFloorHeatLossDetailsFormModel('GroundBelow'),
      getDefaultRoomFloorHeatLossDetailsFormModel('RoomBelow'),
    ];

    if (distinctFloorLevelCodesInOrder.indexOf(roomFloorLevelCode) === 0) {
      defaultFloors[groundIndex].roomAreaPercentage = '100';
    } else {
      defaultFloors[upperIndex].roomAreaPercentage = '100';
    }

    return defaultFloors;
  }

  const floors = unitHeatLossDetails.floorElements.map(() =>
    getNewRoomFloorHeatLossDetailsFormModel(roomFloorLevelCode, externalAirTemperatureInDegreesCelsius, unit),
  );

  if (floors.length !== 0) {
    floors[0].roomAreaPercentage = '100';
  }

  return floors;
};

const getDefaultRoofHeatLossDetails = (
  unitHeatLossDetails: UnitHeatLossDetailsFormModel,
  roomFloorLevelCode: FloorLevelCode | null,
  externalAirTemperatureInDegreesCelsius: number,
  unit: UnitFormModel,
): Array<RoomRoofHeatLossDetailsFormModel> => {
  // Pre-populate room roof details with most likely scenario
  const defaultRoofNames = ['Roof Element 1', 'Ceiling Element 1'];
  if (
    unitHeatLossDetails.roofElements.length === 2 &&
    defaultRoofNames.includes(unitHeatLossDetails.roofElements[0].name) &&
    defaultRoofNames.includes(unitHeatLossDetails.roofElements[1].name)
  ) {
    const distinctFloorLevelCodesInOrder = orderBy(getDistinctFloorLevelCodes(unit), getFloorLevelOrderingPosition);
    const roofIndex = unitHeatLossDetails.roofElements.findIndex(roof => roof.name === 'Roof Element 1');
    const ceilingIndex = unitHeatLossDetails.roofElements.findIndex(roof => roof.name === 'Ceiling Element 1');

    const defaultRoofs = [
      getDefaultRoofHeatLossDetailsFormModel(
        defaultCeilingHeightInMetres,
        externalAirTemperatureInDegreesCelsius.toString(),
      ),
      getDefaultRoofHeatLossDetailsFormModel(defaultCeilingHeightInMetres),
    ];

    if (distinctFloorLevelCodesInOrder.indexOf(roomFloorLevelCode) === distinctFloorLevelCodesInOrder.length - 1) {
      defaultRoofs[roofIndex].roomAreaCoveredPercentage = '100';
    } else {
      defaultRoofs[ceilingIndex].roomAreaCoveredPercentage = '100';
    }

    return defaultRoofs;
  }

  const roofs = unitHeatLossDetails.roofElements.map(() =>
    getNewRoomRoofHeatLossDetailsFormModel(
      defaultCeilingHeightInMetres,
      roomFloorLevelCode,
      externalAirTemperatureInDegreesCelsius,
      unit,
    ),
  );

  if (roofs.length !== 0) {
    roofs[0].roomAreaCoveredPercentage = '100';
  }

  return roofs;
};

const floorConstructionNameIsLoPro10 = (name: string): boolean => {
  const loPro10ConstructionNames = ['LP(150)10', 'ALPRC(150)10', 'ALPRT(150)10', 'ALPB(150)10'];

  return loPro10ConstructionNames.includes(name);
};

const floorCoveringIsCompatibleWithLoPro10 = (name: string | null): boolean => {
  const incompatibleCoverings = ['Resin', 'Parquet', 'Hardwood', 'Polished screed'];

  return name ? !incompatibleCoverings.includes(name) : true;
};

export class RoomHeatLossDetailsFormModelValidator extends Validator<RoomHeatLossDetailsFormModel> {
  constructor(isDraft: boolean, floorConstruction: RoomFloorConstructionFormModel | null) {
    super();

    this.ruleFor('ceilingHeightInMetres')
      .must(beGreaterThanOrEqualTo(0))
      .must(haveDecimalPlaces(2));

    this.ruleFor('ceilingHeightInMetres')
      .notEmpty()
      .unless(dto => isDraft || dto.overrideTotalHeatLossInWatts);

    this.ruleFor('numberOfAirChangesPerHour').must(haveDecimalPlaces(1));

    this.ruleFor('numberOfAirChangesPerHour')
      .notEmpty()
      .must(beGreaterThan(0))
      .unless(dto => isDraft || dto.overrideTotalHeatLossInWatts);

    this.ruleFor('mechanicalVentilationHeatRecoveryEfficiencyPercentage')
      .must(beAnInteger)
      .must(beValidPercentage);

    this.ruleFor('mechanicalVentilationHeatRecoveryEfficiencyPercentage')
      .notEmpty()
      .unless(dto => isDraft || dto.overrideTotalHeatLossInWatts);

    this.ruleFor('floorCoveringName')
      .must(floorCoveringIsCompatibleWithLoPro10)
      .when(() => floorConstruction != null && floorConstructionNameIsLoPro10(floorConstruction.displayName));

    this.ruleFor('floorCoveringName')
      .notNull()
      .unless(dto => isDraft || dto.overrideTotalHeatLossInWatts);

    this.ruleFor('floorCoveringThermalResistancePerMillimetre')
      .must(beGreaterThanOrEqualTo(0))
      .must(beLessThan(100))
      .must(haveDecimalPlaces(4));

    this.ruleFor('floorCoveringThermalResistancePerMillimetre')
      .notEmpty()
      .unless(dto => isDraft || dto.overrideTotalHeatLossInWatts);

    this.ruleFor('floorCoveringThicknessInMillimetres')
      .must(beGreaterThanOrEqualTo(0))
      .must(beLessThan(1000))
      .must(haveDecimalPlaces(1));

    this.ruleFor('floorCoveringThicknessInMillimetres')
      .notEmpty()
      .unless(dto => isDraft || dto.overrideTotalHeatLossInWatts);

    this.ruleFor('deckingThermalResistancePerMillimetre')
      .must(beGreaterThanOrEqualTo(0))
      .must(beLessThan(100))
      .must(haveDecimalPlaces(4));

    this.ruleFor('deckingThermalResistancePerMillimetre')
      .notEmpty()
      .unless(dto => isDraft || dto.overrideTotalHeatLossInWatts);

    this.ruleFor('deckingThicknessInMillimetres')
      .must(beAnInteger)
      .must(beBetweenInclusive(0, 100));

    this.ruleFor('deckingThicknessInMillimetres')
      .notEmpty()
      .unless(dto => isDraft || dto.overrideTotalHeatLossInWatts);

    this.ruleFor('underlayThermalResistancePerMillimetre')
      .must(beGreaterThanOrEqualTo(0))
      .must(beLessThan(100))
      .must(haveDecimalPlaces(4));

    this.ruleFor('underlayThermalResistancePerMillimetre')
      .notEmpty()
      .unless(dto => isDraft || dto.overrideTotalHeatLossInWatts);

    this.ruleFor('underlayThicknessInMillimetres')
      .must(beGreaterThanOrEqualTo(0))
      .must(beLessThan(1000))
      .must(haveDecimalPlaces(1));

    this.ruleFor('underlayThicknessInMillimetres')
      .notEmpty()
      .unless(dto => isDraft || dto.overrideTotalHeatLossInWatts);

    this.ruleFor('userEnteredTotalHeatLossInWatts')
      .must(beNumeric)
      .must(haveDecimalPlaces(2))
      .when(room => room.overrideTotalHeatLossInWatts);

    this.ruleFor('userEnteredTotalHeatLossInWatts')
      .notEmpty()
      .must(beNumeric)
      .must(haveDecimalPlaces(2))
      .when(room => !isDraft && room.overrideTotalHeatLossInWatts);

    this.ruleForEach('walls').setValidator(
      dto => new RoomWallHeatLossDetailsFormModelValidator(!isDraft && !dto.overrideTotalHeatLossInWatts),
    );

    this.ruleForEach('roofs').setValidator(roomHeatLossDetails => {
      const totalRoomAreaCoveredPercentageAcrossAllRoofElements = sumBy(roomHeatLossDetails.roofs, roof =>
        isNaN(Number(roof.roomAreaCoveredPercentage)) ? 0 : Number(roof.roomAreaCoveredPercentage),
      );

      return new RoomRoofHeatLossDetailsFormModelValidator(
        totalRoomAreaCoveredPercentageAcrossAllRoofElements,
        roomHeatLossDetails.ceilingHeightInMetres,
        !isDraft && !roomHeatLossDetails.overrideTotalHeatLossInWatts,
      );
    });

    this.ruleForEach('floors').setValidator(roomHeatLossDetails => {
      const totalRoomAreaPercentageAcrossAllFloorElements = sumBy(roomHeatLossDetails.floors, floor =>
        isNaN(Number(floor.roomAreaPercentage)) ? 0 : Number(floor.roomAreaPercentage),
      );

      return new RoomFloorHeatLossDetailsFormModelValidator(
        totalRoomAreaPercentageAcrossAllFloorElements,
        !isDraft && !roomHeatLossDetails.overrideTotalHeatLossInWatts,
      );
    });
  }
}
